import React, { useState, useEffect, useContext, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  NormalInputFloatingLabel,
  EmailInputFloatingLabel,
} from "reactjs-floating-label-inputs";
import "reactjs-floating-label-inputs/dist/index.css";
import ActiveStepIndicator from "components/shared/activeStepIndicator";
import classNames from "classnames";
import { MobXProviderContext } from "mobx-react";
import "react-phone-number-input/style.css";
import { isMediumOrSmallScreen } from "utils/constants/onboardingStep";
import Autocomplete from "react-google-autocomplete";
import {
  formatInputPhoneNumber,
  isValidPhoneNumber,
} from "utils/helpers/validation";

const Step5 = ({ nextStep }) => {
  const { onboardingStore } = useContext(MobXProviderContext);
  const headingRef = useRef();

  const [clickedButton, setClickedButton] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [disableButton, setDisableButton] = useState(true);
  const [labelFloating, setLabelFloating] = useState(false);
  const [floatingLabels, setFloatingLabels] = useState({
    first_name: false,
    last_name: false,
    confirm_email: false,
    company_name: false,
    phone_number: false,
    street_number: false,
    street_name: false,
    postal_code: false,
    unit_suite: false,
    province: false,
    comment: false,
    delivery_address: false,
  });

  const initialValues = {
    first_name: onboardingStore.user.first_name || "",
    last_name: onboardingStore.user.last_name || "",
    company_name: onboardingStore.location.name || "",
    email: onboardingStore.user.email || "",
    confirm_email: onboardingStore.user.email || "",
    phone_number: onboardingStore.location.contact_number || "",
    street_number: onboardingStore.location.street_number || "",
    street_name: onboardingStore.location.street_name || "",
    city: onboardingStore.location.city || "",
    postal_code: onboardingStore.location.postal_code || "",
    unit_suite:
      onboardingStore.location.suit || onboardingStore.unit_suitee || "",
    province: onboardingStore.location.province || "",
    comment: onboardingStore.location.delivery_instructions || "",
    delivery_address: onboardingStore.delivery_address || "",
  };

  const [mannualAddress, setMannualAddress] = useState(false);

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required("This field needs to be filled"),
    last_name: Yup.string().required("This field needs to be filled"),
    company_name: Yup.string().required("This field needs to be filled"),
    email: Yup.string()
      .email("This field needs to be filled properly")
      .required("This field needs to be filled"),
    confirm_email: Yup.string()
      .oneOf([Yup.ref("email"), null], "This field needs to be filled properly")
      .required("This field needs to be filled"),
    phone_number: Yup.string()
      .required("This field needs to be filled")
      .test(
        "isValidPhoneNumber",
        "This field needs to be filled properly",
        (value) => {
          return isValidPhoneNumber(value);
        }
      ),
    street_number: Yup.string().required("This field needs to be filled"),
    street_name: Yup.string().required("This field needs to be filled"),
    city: Yup.string().required("This field needs to be filled"),
    postal_code: Yup.string()
      .required("This field needs to be filled")
      .test(
        "isValidPostalCode",
        "This field needs to be filled properly",
        (value) => {
          const patternWithSpace = /^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/;
          const patternWithoutSpace = /^[A-Za-z]\d[A-Za-z]\d[A-Za-z]\d$/;

          return (
            value &&
            (patternWithSpace.test(value) || patternWithoutSpace.test(value)) &&
            (value.length === 6 || value.length === 7)
          );
        }
      ),
    province: Yup.string().required("This field needs to be filled"),
    delivery_address: Yup.string().required("This field needs to be filled"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      await formik.validateForm();

      if (formik.isValid && !showErrors) {
        onboardingStore.handleUserDetailsData(values);
        await onboardingStore.dispatchSignUpData();
        nextStep();
      }
    },
  });

  const handlePhoneInputBlur = () => {
    formik.setFieldTouched("phone_number", true);
    setFloatingLabels((prev) => ({
      ...prev,
      phone_number: formik.values.phone_number?.trim() !== "",
    }));
    setLabelFloating(false);
  };

  useEffect(() => {
    if (mannualAddress) {
      const completeAddress = `${
        formik.values?.unit_suite ? `${formik.values?.unit_suite} - ` : ""
      }${formik.values?.street_number} ${formik.values?.street_name}, ${
        formik.values?.city
      }, ${formik.values?.province} ${formik.values?.postal_code}`;

      formik.setFieldValue("delivery_address", completeAddress);
    }

    setFloatingLabels({
      ...floatingLabels,
      ...(formik.values?.street_name && { street_name: true }),
      ...(formik.values?.street_number && { street_number: true }),
      ...(formik.values?.unit_suite && { unit_suite: true }),
      ...(formik.values?.city && { city: true }),
      ...(formik.values?.province && { province: true }),
      ...(formik.values?.postal_code && { postal_code: true }),
    });
  }, [
    mannualAddress,
    formik.values?.unit_suite,
    formik.values?.street_name,
    formik.values?.street_number,
    formik.values?.city,
    formik.values?.province,
    formik.values?.postal_code,
  ]);

  useEffect(() => {
    if (
      !formik.errors?.first_name &&
      !formik.errors?.last_name &&
      !formik.errors?.company_name &&
      !formik.errors?.email &&
      !formik.errors?.confirm_email &&
      !formik.errors?.phone_number &&
      !formik.errors?.street_number &&
      !formik.errors?.street_name &&
      !formik.errors?.city &&
      !formik.errors?.postal_code &&
      !formik.errors?.province &&
      !formik.errors?.delivery_address
    ) {
      setShowErrors(false);
    } else {
      if (clickedButton) setShowErrors(true);
    }
  });

  useEffect(() => {
    const areRequiredFieldsFilled = () => {
      const requiredFields = [
        "first_name",
        "last_name",
        "company_name",
        "email",
        "confirm_email",
        "phone_number",
        "street_number",
        "street_name",
        "city",
        "postal_code",
        "province",
        "delivery_address",
      ];

      return requiredFields.every((field) => !!formik.values[field]);
    };

    setDisableButton(!areRequiredFieldsFilled());
  }, [formik.values, disableButton]);

  useEffect(() => {
    setTimeout(() => {
      headingRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  }, []);

  return (
    <>
      <div className="step-one-content relative flex flex-col" ref={headingRef}>
        <ActiveStepIndicator step={isMediumOrSmallScreen ? 4 : 5} />

        <form onSubmit={formik.handleSubmit}>
          <div className="flex-col flex">
            <div className="expected-bag-text screen-xs:w-full-235 font-inter-semibold">
              Your Details
            </div>

            <div className="w-full screen-lg:w-[452px] screen-md:w-[452px] mt-8 ">
              <div className="screen-lg:flex screen-md:flex gap-6 mb-8">
                <div className="flex-col screen-lg:w-1/2 screen-md:w-1/2 mb-8 screen-lg:mb-0 screen-md:mb-0">
                  <NormalInputFloatingLabel
                    className={`${
                      showErrors &&
                      formik.errors?.first_name &&
                      formik.touched.first_name &&
                      !formik.values.first_name
                        ? "error-border"
                        : ""
                    }`}
                    name="first_name"
                    label={
                      floatingLabels.first_name ? "First name*" : "First Name*"
                    }
                    type="text"
                    value={formik.values.first_name}
                    onChangeValue={(val) =>
                      formik.setFieldValue("first_name", val)
                    }
                    onBlur={() => {
                      formik.setFieldTouched("first_name", true);
                      setFloatingLabels((prev) => ({
                        ...prev,
                        first_name: formik.values.first_name?.trim() !== "",
                      }));
                    }}
                    onFocus={() =>
                      setFloatingLabels({ ...floatingLabels, first_name: true })
                    }
                  />

                  <div className="text-red-400 text-xs font-inter-medium">
                    {showErrors &&
                    formik.errors?.first_name &&
                    formik.touched.first_name &&
                    !formik.values.first_name
                      ? formik.errors?.first_name
                      : null}
                  </div>
                </div>

                <div className="flex-col screen-lg:w-1/2 screen-md:w-1/2 mb-8 screen-lg:mb-0 screen-md:mb-0">
                  <NormalInputFloatingLabel
                    className={`${
                      showErrors &&
                      formik.errors?.last_name &&
                      formik.touched.last_name &&
                      !formik.values.last_name
                        ? "error-border"
                        : ""
                    }`}
                    name="last_name"
                    label={
                      floatingLabels.last_name ? "Last name*" : "Last Name*"
                    }
                    type="text"
                    value={formik.values.last_name}
                    onChangeValue={(val) =>
                      formik.setFieldValue("last_name", val)
                    }
                    onBlur={() => {
                      formik.setFieldTouched("last_name", true);
                      setFloatingLabels((prev) => ({
                        ...prev,
                        last_name: formik.values.last_name?.trim() !== "",
                      }));
                    }}
                    onFocus={() =>
                      setFloatingLabels({ ...floatingLabels, last_name: true })
                    }
                  />

                  <div className="text-red-400 text-xs font-inter-medium">
                    {showErrors &&
                    formik.errors?.last_name &&
                    formik.touched.last_name &&
                    !formik.values.last_name
                      ? formik.errors?.last_name
                      : null}
                  </div>
                </div>
              </div>

              <div className="mb-8">
                <NormalInputFloatingLabel
                  className={`${
                    showErrors &&
                    formik.errors?.company_name &&
                    formik.touched.company_name &&
                    !formik.values.company_name
                      ? "error-border"
                      : ""
                  }`}
                  label={
                    floatingLabels.company_name
                      ? "Company name*"
                      : "Company Name*"
                  }
                  placeholder="Company name*"
                  type="text"
                  value={formik.values.company_name}
                  onChangeValue={(val) =>
                    formik.setFieldValue("company_name", val)
                  }
                  onBlur={() => {
                    formik.setFieldTouched("company_name", true);
                    setFloatingLabels((prev) => ({
                      ...prev,
                      company_name: formik.values.company_name?.trim() !== "",
                    }));
                  }}
                  onFocus={() =>
                    setFloatingLabels({ ...floatingLabels, company_name: true })
                  }
                />

                <div className="text-red-400 text-xs font-inter-medium">
                  {showErrors &&
                  formik.errors?.company_name &&
                  formik.touched.company_name &&
                  !formik.values.company_name
                    ? formik.errors?.company_name
                    : null}
                </div>
              </div>

              <div className="mb-8">
                <EmailInputFloatingLabel
                  className={`${
                    (showErrors &&
                      formik.errors?.email &&
                      formik.touched.email) ||
                    (!formik.values.email && formik.touched.email && showErrors)
                      ? "error-border"
                      : ""
                  }`}
                  label="E-mail*"
                  type="email"
                  value={formik.values.email}
                  onChangeValue={(val) => formik.setFieldValue("email", val)}
                  onBlur={() => formik.setFieldTouched("email", true)}
                />

                <div className="text-red-400 text-xs font-inter-medium">
                  {(formik.errors?.email &&
                    formik.touched.email &&
                    showErrors) ||
                  (!formik.values.email && formik.touched.email && showErrors)
                    ? formik.errors?.email
                    : null}
                </div>
              </div>

              <div className="mb-8">
                <EmailInputFloatingLabel
                  className={`${
                    (showErrors &&
                      formik.errors?.confirm_email &&
                      formik.touched.confirm_email) ||
                    (!formik.values.confirm_email &&
                      formik.touched.confirm_email &&
                      showErrors)
                      ? "error-border"
                      : ""
                  }`}
                  label={
                    floatingLabels.confirm_email
                      ? "Confirm e-mail*"
                      : "Confirm E-mail*"
                  }
                  type="email"
                  value={formik.values.confirm_email}
                  onChangeValue={(val) =>
                    formik.setFieldValue("confirm_email", val)
                  }
                  onBlur={() => {
                    formik.setFieldTouched("confirm_email", true);
                    setFloatingLabels((prev) => ({
                      ...prev,
                      confirm_email: formik.values.confirm_email?.trim() !== "",
                    }));
                  }}
                  onFocus={() =>
                    setFloatingLabels({
                      ...floatingLabels,
                      confirm_email: true,
                    })
                  }
                />

                <div className="text-red-400 text-xs font-inter-medium">
                  {(formik.errors?.confirm_email &&
                    formik.touched.confirm_email &&
                    showErrors) ||
                  (!formik.values.confirm_email &&
                    formik.touched.confirm_email &&
                    showErrors)
                    ? formik.errors?.confirm_email
                    : null}
                </div>
              </div>

              <div className="mb-1">
                <NormalInputFloatingLabel
                  label={
                    floatingLabels.phone_number
                      ? "Phone number*"
                      : "Phone Number*"
                  }
                  value={formik.values.phone_number}
                  onChangeValue={(val) => {
                    const isDeleting =
                      val.length < formik.values.phone_number.length;
                    const formattedPhone = formatInputPhoneNumber(val, {
                      isDeleting,
                    });

                    formik.setFieldValue("phone_number", formattedPhone);
                  }}
                  onBlur={handlePhoneInputBlur}
                  onFocus={() =>
                    setFloatingLabels({ ...floatingLabels, phone_number: true })
                  }
                />

                <div className="text-red-400 text-xs font-inter-medium">
                  {showErrors &&
                  formik.errors?.phone_number &&
                  formik.touched.phone_number &&
                  !formik.values.phone_number
                    ? formik.errors?.phone_number
                    : null}

                  {showErrors &&
                  formik.values.phone_number &&
                  !isValidPhoneNumber(String(formik.values.phone_number)) &&
                  formik.touched.phone_number
                    ? "This field needs to be filled properly"
                    : null}
                </div>
              </div>

              {!mannualAddress && (
                <div className="mb-8 autocomplete">
                  <div
                    className={classNames(
                      "text-xs text-slate-700 pb-3 font-inter-normal",
                      {
                        "opacity-0 translate-y-2":
                          !floatingLabels.delivery_address,
                        "opacity-100 translate-y-0":
                          floatingLabels.delivery_address,
                      },
                      "transition-opacity transition-transform duration-600 ease-in-out"
                    )}
                  >
                    Delivery address*
                  </div>

                  <Autocomplete
                    className={`floating-label-input-container w-full px-2 ${
                      showErrors &&
                      formik.errors?.delivery_address &&
                      formik.touched.delivery_address &&
                      !formik.values.delivery_address
                        ? "error-border"
                        : ""
                    }`}
                    name="delivery_address"
                    label={
                      floatingLabels.delivery_address
                        ? "Delivery address*"
                        : "Delivery Address*"
                    }
                    placeholder={
                      floatingLabels.delivery_address ? "" : "Delivery Address*"
                    }
                    apiKey={process.env.REACT_APP_GOOGLE_MAP_KEY}
                    options={{
                      componentRestrictions: { country: "CA" },
                      fields: [
                        "address_components",
                        "geometry",
                        "formatted_address",
                      ],
                      types: ["address"],
                    }}
                    onPlaceSelected={(place, inputRef, autocomplete) => {
                      const _address = place?.address_components?.reduce(
                        (seed, { long_name, types }) => (
                          types.forEach((t) => (seed[t] = long_name)), seed
                        ),
                        {}
                      );

                      formik.setFieldValue(
                        "delivery_address",
                        place?.formatted_address
                      );
                      formik.setFieldValue(
                        "street_number",
                        _address?.street_number
                      );
                      formik.setFieldValue("street_name", _address?.route);
                      formik.setFieldValue("city", _address?.locality);
                      formik.setFieldValue(
                        "postal_code",
                        _address?.postal_code
                      );
                      formik.setFieldValue(
                        "province",
                        _address?.administrative_area_level_1
                      );
                      formik.setFieldValue("unit_suite", _address?.subpremise);
                      setMannualAddress(true);
                    }}
                    onBlur={() => {
                      formik.setFieldTouched("delivery_address", true);
                      setFloatingLabels((prev) => ({
                        ...prev,
                        delivery_address:
                          formik.values.delivery_address?.trim() !== "",
                      }));
                    }}
                    onFocus={() =>
                      setFloatingLabels({
                        ...floatingLabels,
                        delivery_address: true,
                      })
                    }
                  />

                  <div className="text-red-400 text-xs font-inter-medium">
                    {showErrors &&
                    formik.errors?.delivery_address &&
                    formik.touched.delivery_address &&
                    !formik.values.delivery_address
                      ? formik.errors?.delivery_address
                      : null}
                  </div>
                </div>
              )}

              {mannualAddress && (
                <>
                  <div className="mb-8 mt-7">
                    <NormalInputFloatingLabel
                      className={`${
                        showErrors &&
                        formik.errors?.street_number &&
                        formik.touched.street_number &&
                        !formik.values.street_number
                          ? "error-border"
                          : ""
                      }`}
                      name="street_number"
                      label={
                        floatingLabels.street_number
                          ? "Street number*"
                          : "Street Number*"
                      }
                      type="text"
                      inputMode="numeric"
                      pattern="\d*"
                      value={formik.values.street_number}
                      onChangeValue={(val) => {
                        const regex = /^\d*$/;
                        if (regex.test(val)) {
                          formik.setFieldValue("street_number", val);
                        }
                      }}
                      onBlur={() => {
                        formik.setFieldTouched("street_number", true);
                        setFloatingLabels((prev) => ({
                          ...prev,
                          street_number:
                            formik.values.street_number?.trim() !== "",
                        }));
                      }}
                      onFocus={() =>
                        setFloatingLabels({
                          ...floatingLabels,
                          street_number: true,
                        })
                      }
                    />

                    <div className="text-red-400 text-xs font-inter-medium">
                      {showErrors &&
                      formik.errors?.street_number &&
                      formik.touched.street_number &&
                      !formik.values.street_number
                        ? formik.errors?.street_number
                        : null}
                    </div>
                  </div>

                  <div className="mb-8">
                    <NormalInputFloatingLabel
                      className={`${
                        showErrors &&
                        formik.errors?.street_name &&
                        formik.touched.street_name &&
                        !formik.values.street_name
                          ? "error-border"
                          : ""
                      }`}
                      name="street_name"
                      label={
                        floatingLabels.street_name
                          ? "Street name*"
                          : "Street Name*"
                      }
                      type="text"
                      value={formik.values.street_name}
                      onChangeValue={(val) =>
                        formik.setFieldValue("street_name", val)
                      }
                      onBlur={() => {
                        formik.setFieldTouched("street_name", true);
                        setFloatingLabels((prev) => ({
                          ...prev,
                          street_name: formik.values.street_name?.trim() !== "",
                        }));
                      }}
                      onFocus={() =>
                        setFloatingLabels({
                          ...floatingLabels,
                          street_name: true,
                        })
                      }
                    />

                    <div className="text-red-400 text-xs font-inter-medium">
                      {showErrors &&
                      formik.errors?.street_name &&
                      formik.touched.street_name &&
                      !formik.values.street_name
                        ? formik.errors?.street_name
                        : null}
                    </div>
                  </div>

                  <div className="mb-8">
                    <NormalInputFloatingLabel
                      className={`${
                        showErrors &&
                        formik.errors?.city &&
                        formik.touched.city &&
                        !formik.values.city
                          ? "error-border"
                          : ""
                      }`}
                      label="City*"
                      type="text"
                      value={formik.values.city}
                      onChangeValue={(val) => formik.setFieldValue("city", val)}
                      onBlur={() => formik.setFieldTouched("city", true)}
                    />

                    <div className="text-red-400 text-xs font-inter-medium">
                      {showErrors &&
                      formik.errors?.city &&
                      formik.touched.city &&
                      !formik.values.city
                        ? formik.errors?.city
                        : null}
                    </div>
                  </div>

                  <div className="flex gap-6 mb-8">
                    <div className="w-1/2">
                      <NormalInputFloatingLabel
                        className={`${
                          showErrors &&
                          formik.errors?.province &&
                          formik.touched.province &&
                          !formik.values.province
                            ? "error-border"
                            : ""
                        }`}
                        label="Province*"
                        type="text"
                        value={formik.values.province}
                        onChangeValue={(val) =>
                          formik.setFieldValue("province", val)
                        }
                        onBlur={() => formik.setFieldTouched("province", true)}
                      />

                      <div className="text-red-400 text-xs font-inter-medium">
                        {showErrors &&
                        formik.errors?.province &&
                        formik.touched.province &&
                        !formik.values.province
                          ? formik.errors?.province
                          : null}
                      </div>
                    </div>

                    <div className="w-1/2">
                      <NormalInputFloatingLabel
                        className={`${
                          showErrors &&
                          formik.errors?.postal_code &&
                          formik.touched.postal_code &&
                          formik.values.postal_code
                            ? "error-border"
                            : ""
                        }`}
                        label={
                          floatingLabels.postal_code
                            ? "Postal code*"
                            : "Postal Code*"
                        }
                        type="text"
                        value={formik.values.postal_code}
                        onChangeValue={(val) =>
                          formik.setFieldValue("postal_code", val)
                        }
                        onBlur={() => {
                          formik.setFieldTouched("postal_code", true);
                          setFloatingLabels((prev) => ({
                            ...prev,
                            postal_code:
                              formik.values.postal_code?.trim() !== "",
                          }));
                        }}
                        onFocus={() =>
                          setFloatingLabels({
                            ...floatingLabels,
                            postal_code: true,
                          })
                        }
                      />

                      <div className="text-red-400 text-xs font-inter-medium">
                        {showErrors &&
                        formik.errors?.postal_code &&
                        formik.touched.postal_code &&
                        formik.values.postal_code
                          ? formik.errors?.postal_code
                          : null}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/*optional field*/}
              <NormalInputFloatingLabel
                className="mb-8"
                label={
                  floatingLabels.unit_suite
                    ? "Unit/Suite (optional)"
                    : "Unit/suite (optional)"
                }
                value={formik.values.unit_suite}
                onChangeValue={(val) => {
                  formik.setFieldValue("unit_suite", val);
                }}
                onBlur={() => {
                  setFloatingLabels((prev) => ({
                    ...prev,
                    unit_suite: formik.values.unit_suite?.trim() !== "",
                  }));
                }}
                onFocus={() =>
                  setFloatingLabels({ ...floatingLabels, unit_suite: true })
                }
              />

              {/*optional field*/}
              <NormalInputFloatingLabel
                className="mb-8"
                label={
                  floatingLabels.comment
                    ? "Special delivery instructions (optional)"
                    : "Special Delivery Instructions (optional)"
                }
                type="text"
                value={formik.values.comment}
                onChangeValue={(val) => formik.setFieldValue("comment", val)}
                onBlur={() => {
                  setFloatingLabels((prev) => ({
                    ...prev,
                    comment: formik.values.comment?.trim() !== "",
                  }));
                }}
                onFocus={() =>
                  setFloatingLabels({ ...floatingLabels, comment: true })
                }
              />
            </div>
          </div>

          <div className="bottom-fixed-div object-bottom mt-4 mb-4">
            <button
              className={classNames("button-class font-inter-medium", {
                "bg-gray-200": disableButton,
                "bg-indigo-900": !disableButton,
              })}
              type="submit"
              disabled={disableButton}
              onClick={() => setClickedButton(true)}
            >
              Continue
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default Step5;
